import {
  FlexBox,
  getSelectionListContext,
  Icon,
  Spinner,
} from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { AsyncStatus } from 'lib-shared-types-api';
import { PropsWithChildren } from 'lib-shared-types-utils';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useEffectOnce } from 'react-use';

import { CategorizedListProps } from 'components/categorizedList';

import styles from './createItem.module.scss';
import { CreateFn, getCreateItemContext } from './createItemContext';

type CreateItemProps = PropsWithChildren &
  Pick<CategorizedListProps, 'refetch'> & {
    /** The search term. */
    searchTerm: string;
  };

/** Represents the create item. */
export const CreateItem = <TItem,>({
  searchTerm,
  refetch,
  children,
}: CreateItemProps): JSX.Element => {
  const ref = useRef<HTMLLIElement>(null);
  const CreateItemContext = getCreateItemContext<TItem>();
  const { item: itemContext } = useContext(getSelectionListContext<TItem>());
  const [asyncStatus, setAsyncStatus] = useState<AsyncStatus>('uninitialized');
  const [create, setCreate] = useState<CreateFn<TItem>>();

  // Handles registering the custom item with the selection list.
  useEffectOnce(() => {
    itemContext.register({ isActivatable: true });
    return () => itemContext.register({ isActivatable: false });
  });

  /** Handles creating an item. */
  const handleCreate = useCallback(async () => {
    if (create && asyncStatus !== 'pending') {
      setAsyncStatus('pending');
      const value = await create();

      if (value) {
        // We do this in a timeout to ensure the backend is updated after which
        // we retrieve the categories anew.
        setTimeout(() => {
          itemContext.onSelect(value);
          refetch();
          setAsyncStatus('fulfilled');
        }, 500);
      } else {
        setAsyncStatus('rejected');
      }
    }
  }, [asyncStatus, itemContext, create, refetch]);

  /** Handles the enter event after which an item should be created. */
  const handleKeydown = useCallback(
    async (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        handleCreate();
      }
    },
    [handleCreate],
  );

  // Handles the keydown event registration.
  useEffect(() => {
    if (itemContext.isActive) {
      addEventListener('keydown', handleKeydown);
    } else {
      removeEventListener('keydown', handleKeydown);
    }

    return () => removeEventListener('keydown', handleKeydown);
  }, [itemContext.isActive, handleKeydown]);

  // Handles scrolling the item into view when active.
  useEffect(() => {
    if (itemContext.isActive) {
      ref.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start',
      });
    }
  }, [itemContext.isActive]);

  return (
    <CreateItemContext.Provider value={{ searchTerm, setCreate }}>
      <li ref={ref}>
        <FlexBox
          justifyContent="space-between"
          gap="200"
          padding="200 300"
          className={classnames(styles.root, {
            [styles.active]: itemContext.isActive,
          })}
          onClick={handleCreate}
        >
          {children}
          <FlexBox alignItems="center">
            {asyncStatus !== 'pending' && <Icon name="plus" />}
            {asyncStatus === 'pending' && <Spinner size={16} />}
          </FlexBox>
        </FlexBox>
      </li>
    </CreateItemContext.Provider>
  );
};
